import { useEffect, useState, useRef } from "react";
import { api, INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { Link } from "react-router-dom";
import { Search, Plus, Upload, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ELEVATED = ["admin", "dept_lead"];
const EXPORT_ALLOWLIST_EMAILS = ["bhrigu@fuelled.in"];
const TIERS = ["Base", "Pro", "Pro Advanced", "Legend"];
const STAGES = ["Approved", "Paused"];
const CREATED_PRESETS = [
  { value: "", label: "All time" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];
const SORT_OPTIONS = [
  { value: "", label: "Default (newest)" },
  { value: "name_asc", label: "Name: A → Z" },
  { value: "name_desc", label: "Name: Z → A" },
  { value: "new_customers_desc", label: "New customers: High → Low" },
  { value: "new_customers_asc", label: "New customers: Low → High" },
  { value: "revenue_desc", label: "Revenue: High → Low" },
  { value: "revenue_asc", label: "Revenue: Low → High" },
  { value: "lifetime_desc", label: "Lifetime sales: High → Low" },
  { value: "lifetime_asc", label: "Lifetime sales: Low → High" },
];

export default function Affiliates() {
  const { user } = useAuth();
  const canFilterByAsm = user && ELEVATED.includes(user.role);
  const canImport = user && ELEVATED.includes(user.role);
  const canExport = user && (user.role === "admin" || EXPORT_ALLOWLIST_EMAILS.includes((user.email || "").toLowerCase()));
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [stage, setStage] = useState("");
  const [starterKit, setStarterKit] = useState("");
  const [asmId, setAsmId] = useState("");
  const [createdPreset, setCreatedPreset] = useState("");
  const [createdAfter, setCreatedAfter] = useState("");
  const [createdBefore, setCreatedBefore] = useState("");
  const [launchAfter, setLaunchAfter] = useState("");
  const [launchBefore, setLaunchBefore] = useState("");
  const [onbAfter, setOnbAfter] = useState("");
  const [onbBefore, setOnbBefore] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [asms, setAsms] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);

  const buildParams = () => {
    const params = {};
    if (q) params.q = q;
    if (tier) params.tier = tier;
    if (stage) params.stage = stage;
    if (starterKit) params.starter_kit = starterKit;
    if (asmId) params.asm_id = asmId;
    if (createdPreset && createdPreset !== "custom") {
      params.created_within_days = Number(createdPreset);
    } else if (createdPreset === "custom") {
      if (createdAfter) params.created_after = new Date(createdAfter).toISOString();
      if (createdBefore) params.created_before = new Date(createdBefore + "T23:59:59").toISOString();
    }
    if (launchAfter) params.launch_call_after = new Date(launchAfter).toISOString();
    if (launchBefore) params.launch_call_before = new Date(launchBefore + "T23:59:59").toISOString();
    if (onbAfter) params.onboarding_call_after = new Date(onbAfter).toISOString();
    if (onbBefore) params.onboarding_call_before = new Date(onbBefore + "T23:59:59").toISOString();
    return params;
  };

  const load = () => {
    api.get("/affiliates", { params: buildParams() }).then((r) => setList(r.data));
  };

  useEffect(() => { load(); }, [tier, stage, starterKit, asmId, q, createdPreset, createdAfter, createdBefore, launchAfter, launchBefore, onbAfter, onbBefore]);
  useEffect(() => { api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm"))); }, []);

  const sortedList = (() => {
    if (!sortBy) return list;
    const arr = [...list];
    if (sortBy === "name_asc") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "name_desc") {
      arr.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    } else {
      let key;
      if (sortBy.startsWith("new_customers")) key = "current_month_new_customers";
      else if (sortBy.startsWith("lifetime")) key = "lifetime_sales";
      else key = "current_month_sales";
      const dir = sortBy.endsWith("_asc") ? 1 : -1;
      arr.sort((a, b) => ((a[key] ?? 0) - (b[key] ?? 0)) * dir);
    }
    return arr;
  })();

  return (
    <div>
      <PageHeader
        title="Affiliates"
        subtitle={`${list.length} affiliates`}
        actions={
          <div className="flex flex-wrap gap-2">
            {canExport && <ExportAffiliatesMenu filters={buildParams()} activeFilterCount={Object.values({ tier, stage, starterKit, asmId, q, createdPreset, launchAfter, launchBefore, onbAfter, onbBefore }).filter(Boolean).length} />}
            {canImport && <ImportAffiliatesButton asms={asms} onDone={load} />}
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button data-testid="new-affiliate-btn" className="rounded-full h-10 px-4 md:px-5">
                  <Plus className="h-4 w-4 mr-1.5" /> New
                </Button>
              </DialogTrigger>
              <NewAffiliateDialog asms={asms} onCreated={() => { setOpenCreate(false); load(); }} />
            </Dialog>
          </div>
        }
      />

      <div className="p-4 md:p-10">
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search affiliates…"
              className="pl-9 h-10 rounded-full w-64"
            />
          </div>
          <select data-testid="filter-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
            <option value="">All tiers</option>
            {TIERS.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select data-testid="filter-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="filter-starter-kit" value={starterKit} onChange={(e) => setStarterKit(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
            <option value="">Starter kit: All</option>
            <option value="yes">Starter kit: Purchased</option>
            <option value="no">Starter kit: Not purchased</option>
          </select>
          {canFilterByAsm && (
            <select data-testid="filter-asm" value={asmId} onChange={(e) => setAsmId(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
              <option value="">All ASMs</option>
              {asms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select data-testid="filter-created" value={createdPreset} onChange={(e) => setCreatedPreset(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
            {CREATED_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {createdPreset === "custom" && (
            <>
              <Input
                data-testid="filter-created-after"
                type="date"
                value={createdAfter}
                onChange={(e) => setCreatedAfter(e.target.value)}
                className="h-10 rounded-full w-40"
                placeholder="From"
              />
              <Input
                data-testid="filter-created-before"
                type="date"
                value={createdBefore}
                onChange={(e) => setCreatedBefore(e.target.value)}
                className="h-10 rounded-full w-40"
                placeholder="To"
              />
            </>
          )}
          <button
            type="button"
            data-testid="toggle-more-filters"
            onClick={() => setShowMoreFilters((v) => !v)}
            className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium hover:bg-secondary transition-colors"
          >
            {showMoreFilters ? "Hide call filters" : "Call date filters"}
            {(launchAfter || launchBefore || onbAfter || onbBefore) && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5">
                {[launchAfter || launchBefore, onbAfter || onbBefore].filter(Boolean).length}
              </span>
            )}
          </button>
          <select
            data-testid="filter-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {showMoreFilters && (
          <div className="flex flex-wrap items-end gap-4 mb-6 rounded-2xl border border-border/60 bg-secondary/20 p-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Launch call date</div>
              <div className="flex gap-2">
                <Input
                  data-testid="filter-launch-after"
                  type="date"
                  value={launchAfter}
                  onChange={(e) => setLaunchAfter(e.target.value)}
                  className="h-10 rounded-full w-40"
                  placeholder="From"
                />
                <Input
                  data-testid="filter-launch-before"
                  type="date"
                  value={launchBefore}
                  onChange={(e) => setLaunchBefore(e.target.value)}
                  className="h-10 rounded-full w-40"
                  placeholder="To"
                />
                {(launchAfter || launchBefore) && (
                  <button
                    type="button"
                    data-testid="clear-launch-dates"
                    onClick={() => { setLaunchAfter(""); setLaunchBefore(""); }}
                    className="h-10 rounded-full px-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Onboarding call date</div>
              <div className="flex gap-2">
                <Input
                  data-testid="filter-onb-after"
                  type="date"
                  value={onbAfter}
                  onChange={(e) => setOnbAfter(e.target.value)}
                  className="h-10 rounded-full w-40"
                  placeholder="From"
                />
                <Input
                  data-testid="filter-onb-before"
                  type="date"
                  value={onbBefore}
                  onChange={(e) => setOnbBefore(e.target.value)}
                  className="h-10 rounded-full w-40"
                  placeholder="To"
                />
                {(onbAfter || onbBefore) && (
                  <button
                    type="button"
                    data-testid="clear-onb-dates"
                    onClick={() => { setOnbAfter(""); setOnbBefore(""); }}
                    className="h-10 rounded-full px-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Affiliate</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Tier</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">ASM</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Stage</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">MTD Sales</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Lifetime Sales</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">New Cust</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">%</th>
              </tr>
            </thead>
            <tbody>
              {sortedList.map((a) => {
                const asm = asms.find((u) => u.id === a.assigned_asm_id);
                return (
                  <tr key={a.id} className="border-t border-border/50 hover:bg-secondary/30 transition-colors">
                    <td className="px-6 py-4">
                      <Link to={`/affiliates/${a.id}`} data-testid={`affiliate-row-${a.id}`} className="flex items-center gap-3">
                        {a.avatar_url ? (
                          <img src={a.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold">{a.name.charAt(0)}</div>
                        )}
                        <div>
                          <div className="text-sm font-semibold">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.instagram || a.city || "—"}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4"><TierBadge tier={a.tier} /></td>
                    <td className="px-6 py-4 text-sm">{asm?.name || "—"}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{a.onboarding_stage}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium">{INR(a.current_month_sales)}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium">{INR(a.lifetime_sales || 0)}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium">{a.current_month_new_customers}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-primary">{a.current_month_commission_percent}%</td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-sm text-muted-foreground">No affiliates match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function NewAffiliateDialog({ asms, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", instagram: "", niche: "", followers: 0,
    assigned_asm_id: asms[0]?.id || "",
    code: "", referral_link: "", tier: "Base", onboarding_call_at: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      // Normalize empties
      if (!payload.code?.trim()) delete payload.code;
      if (!payload.referral_link?.trim()) delete payload.referral_link;
      if (!payload.onboarding_call_at?.trim()) delete payload.onboarding_call_at;
      else payload.onboarding_call_at = new Date(payload.onboarding_call_at).toISOString();
      if (payload.code) payload.code = payload.code.trim().toUpperCase();
      await api.post("/affiliates", payload);
      toast.success("Affiliate created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <DialogContent className="rounded-3xl max-w-2xl">
      <DialogHeader>
        <DialogTitle>New Affiliate</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Name *</Label><Input data-testid="new-aff-name" value={form.name} onChange={set("name")} className="mt-1 rounded-xl" /></div>
        <div><Label>Email</Label><Input data-testid="new-aff-email" value={form.email} onChange={set("email")} className="mt-1 rounded-xl" /></div>
        <div><Label>Phone</Label><Input data-testid="new-aff-phone" value={form.phone} onChange={set("phone")} className="mt-1 rounded-xl" /></div>
        <div><Label>City</Label><Input value={form.city} onChange={set("city")} className="mt-1 rounded-xl" /></div>
        <div><Label>Instagram</Label><Input value={form.instagram} onChange={set("instagram")} className="mt-1 rounded-xl" /></div>
        <div><Label>Niche</Label><Input value={form.niche} onChange={set("niche")} className="mt-1 rounded-xl" /></div>
        <div><Label>Followers</Label><Input type="number" value={form.followers} onChange={set("followers")} className="mt-1 rounded-xl" /></div>

        {/* Program setup */}
        <div className="col-span-2 mt-2 border-t border-border/60 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Program Setup</div>
        </div>
        <div>
          <Label>Affiliate Code</Label>
          <Input data-testid="new-aff-code" placeholder="e.g. RIYA16" value={form.code} onChange={set("code")} className="mt-1 rounded-xl uppercase" />
          <div className="text-[10px] text-muted-foreground mt-1">Auto-uppercased. Leave empty to add later.</div>
        </div>
        <div>
          <Label>Tier</Label>
          <select data-testid="new-aff-tier" value={form.tier} onChange={set("tier")} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
            <option value="Base">Base</option>
            <option value="Pro">Pro</option>
            <option value="Pro Advanced">Pro Advanced</option>
            <option value="Legend">Legend</option>
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Defaults to Base. Recompute may auto-promote later.</div>
        </div>
        <div className="col-span-2">
          <Label>Affiliate Link</Label>
          <Input data-testid="new-aff-link" placeholder="https://fuelled.in/?ref=RIYA16" value={form.referral_link} onChange={set("referral_link")} className="mt-1 rounded-xl" />
        </div>
        <div className="col-span-2">
          <Label>Onboarding Call Date</Label>
          <Input data-testid="new-aff-call" type="datetime-local" value={form.onboarding_call_at} onChange={set("onboarding_call_at")} className="mt-1 rounded-xl" />
          <div className="text-[10px] text-muted-foreground mt-1">Arms the 30-day Fast Track window (2 new customers → ₹2,000 MRP reward).</div>
        </div>

        <div className="col-span-2 mt-2 border-t border-border/60 pt-3">
          <Label>Assign ASM</Label>
          <select data-testid="new-aff-asm" value={form.assigned_asm_id} onChange={set("assigned_asm_id")} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
            {asms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="submit-new-aff" onClick={submit} disabled={busy} className="rounded-full">
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


function ImportAffiliatesButton({ asms, onDone }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [asmId, setAsmId] = useState("");

  const submit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const params = { only_approved: true };
      if (asmId) params.assign_to_asm_id = asmId;
      const { data } = await api.post("/affiliates/import", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created_affiliates} new, updated ${data.updated_affiliates}. ${data.created_codes} codes added.`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <>
      <input type="file" accept=".csv" ref={fileRef} onChange={submit} className="hidden" data-testid="import-aff-file" />
      <select data-testid="import-asm-assign" value={asmId} onChange={(e) => setAsmId(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
        <option value="">Assign ASM: (none)</option>
        {asms.map((u) => <option key={u.id} value={u.id}>Assign → {u.name}</option>)}
      </select>
      <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-full h-10" data-testid="import-aff-btn">
        <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Importing…" : "Import CSV"}
      </Button>
      {result && (
        <Dialog open={!!result} onOpenChange={() => setResult(null)}>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle>Import complete</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div>✅ New affiliates: <span className="font-semibold">{result.created_affiliates}</span></div>
              <div>♻️ Updated affiliates: <span className="font-semibold">{result.updated_affiliates}</span></div>
              <div>🎟️ New codes: <span className="font-semibold">{result.created_codes}</span></div>
              <div>⏭️ Skipped codes (already existed): <span className="font-semibold">{result.skipped_codes}</span></div>
              {result.total_errors > 0 && (
                <div>
                  <div className="text-amber-600 font-semibold mt-2">⚠️ {result.total_errors} row issues</div>
                  <ul className="text-xs max-h-40 overflow-y-auto mt-1 space-y-0.5">
                    {result.errors.map((e, i) => (
                      <li key={i} className="text-muted-foreground">Row {e.row}: {e.error || e.warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter><Button onClick={() => setResult(null)} className="rounded-full">Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ExportAffiliatesMenu({ filters, activeFilterCount = 0 }) {
  const download = async (format) => {
    try {
      const params = { format, ...filters };
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
      const res = await api.get("/export/affiliates", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const suffix = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}-${String(v).replace(/\s/g, "_")}`).join("_");
      a.download = `affiliates-tiers${suffix ? "-" + suffix : ""}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex gap-1 rounded-full border border-border bg-card p-1 items-center">
      {activeFilterCount > 0 && (
        <span data-testid="export-filter-count" className="text-[10px] text-primary font-semibold px-2">
          {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
        </span>
      )}
      <button data-testid="export-csv-btn" onClick={() => download("csv")} className="rounded-full px-3 h-8 text-xs font-semibold hover:bg-secondary transition-colors flex items-center gap-1.5">
        <FileDown className="h-3.5 w-3.5" /> CSV
      </button>
      <button data-testid="export-pdf-btn" onClick={() => download("pdf")} className="rounded-full px-3 h-8 text-xs font-semibold hover:bg-secondary transition-colors flex items-center gap-1.5">
        <FileDown className="h-3.5 w-3.5" /> PDF
      </button>
    </div>
  );
}
